import Modal from "react-modal";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  useAddVideoMutation,
  useGetVideoQuery,
  useUpdateVideoMutation,
} from "@features/videos/videosApi";
import {
  useGetAssignmentsQuery,
  useUpdateAssignmentMutation,
} from "@features/assignments/assignmentsApi";
import {
  useGetQuizzesQuery,
  useUpdateQuizMutation,
} from "@features/quizzes/quizzesApi";
import InputWrapper from "@common/components/InputWrapper";

const VideoModal = ({ videoId = "", open, closeModal }) => {
  const [data, setData] = useState({
    title: "",
    description: "",
    url: "",
    views: "",
    duration: "",
    createdAt: new Date(),
  });

  const { data: assignments } = useGetAssignmentsQuery();
  const { data: quizzes } = useGetQuizzesQuery();

  const [addVideo, { isLoading }] = useAddVideoMutation();

  const [updateVideo, { isLoading: isUpdateLoading }] =
    useUpdateVideoMutation();

  const [updateAssignment] = useUpdateAssignmentMutation();
  const [updateQuiz] = useUpdateQuizMutation();

  const { data: video } = useGetVideoQuery(videoId);

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
    },
  };

  Modal.setAppElement("#root");

  useEffect(() => {
    const { title, description, url, views, duration, createdAt } = video || {};
    if (videoId) {
      setData({
        title,
        description,
        url,
        views,
        duration,
        createdAt,
      });
    }
  }, [video, videoId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const resetForm = () => {
    setData({
      title: "",
      description: "",
      url: "",
      views: "",
      duration: "",
      createdAt: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const response = await addVideo(data);
    if (response?.data) {
      resetForm();
      closeModal();
      toast.success("Video added successfully!");
    } else {
      toast.error("Video added failed!");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const assignmentToUpdate = assignments?.find(
      (item) => item.video_id === videoId
    );

    const quizToUpdate = quizzes?.find((item) => item.video_id === videoId);

    const updatedData = {
      video_title: data?.title,
    };

    const response = await updateVideo({ id: videoId, data });

    if (response?.data) {
      closeModal();
      toast.success("Video updated successfully!");
      updateAssignment({ id: assignmentToUpdate?.id, data: updatedData });
      updateQuiz({ id: quizToUpdate?.id, data: updatedData });
    } else {
      toast.error("Video updated failed!");
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Example Modal"
    >
      <h2 className="text-xl text-center font-bold">
        {videoId ? "Update" : "Add"} Video
      </h2>
      <button className="btn-modal-close" onClick={closeModal}>
        &times;
      </button>
      <form
        className="space-y-6"
        onSubmit={videoId ? handleUpdate : handleSubmit}
      >
        <div className="rounded-md shadow-sm -space-y-px">
          <InputWrapper
            id="title"
            name="title"
            type="text"
            required
            className="form-input rounded"
            placeholder="Video Title"
            value={data?.title}
            onChange={handleChange}
          />

          <div style={{ marginBottom: "-7px" }}>
            <label htmlFor="description" className="form-label sr-only">
              Description
            </label>
            <textarea
              name="description"
              id="description"
              rows="3"
              className="form-input rounded mt-4"
              placeholder="Video Description"
              value={data?.description}
              onChange={handleChange}
              required
            ></textarea>
          </div>

          <InputWrapper
            id="url"
            name="url"
            type="url"
            required
            className="form-input rounded"
            placeholder="Video URL"
            value={data?.url}
            onChange={handleChange}
          />

          <InputWrapper
            id="views"
            name="views"
            type="text"
            required
            className="form-input rounded"
            placeholder="Views"
            value={data?.views}
            onChange={handleChange}
          />

          <InputWrapper
            id="duration"
            name="duration"
            type="text"
            required
            className="form-input rounded"
            placeholder="Duration"
            value={data?.duration}
            onChange={handleChange}
          />

          <div className="submit-button-wrapper text-center">
            <button
              type="submit"
              disabled={videoId ? isUpdateLoading : isLoading}
              className="btn-submit font-bold w-full"
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default VideoModal;
